import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { MAX_NAME_LENGTH } from '@bm/shared';
import { de } from '@/i18n/de';
import { useAppStore } from '@/lib/store';
import { wsClient } from '@/lib/wsClient';
import { recallName } from '@/lib/recentName';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/Button';

interface RejoinScreenProps {
  onBack: () => void;
}

/**
 * Der Weg zurück ins laufende Spiel ohne gültiges Token.
 *
 * Gesucht wird über den eigenen Namen, nicht über eine Liste laufender Räume:
 * Die öffentliche Lobby zeigt weiterhin ausschließlich offene Spiele (GDD §31),
 * und wer den Namen nicht kennt, erfährt hier nichts.
 */
export function RejoinScreen({ onBack }: RejoinScreenProps) {
  const [name, setName] = useState(recallName);
  const [searched, setSearched] = useState(false);
  const options = useAppStore((s) => s.rejoinOptions);
  const rejoin = useAppStore((s) => s.rejoin);
  const trimmed = name.trim();

  // Der häufigste Fall ist „derselbe Mensch, neuer Tab": Steht der Name schon da,
  // wird auch gleich gesucht — sonst wäre der erste Schritt ein Tap auf einen
  // Button, dessen Feld bereits ausgefüllt ist.
  useEffect(() => {
    const remembered = recallName().trim();
    if (remembered.length === 0) return;
    wsClient.send({ type: 'rejoin:lookup', playerName: remembered });
    setSearched(true);
  }, []);

  // Ein Verlassen des Screens soll keine offene Anfrage zurücklassen.
  useEffect(() => {
    return () => {
      if (useAppStore.getState().rejoin?.status === 'PENDING') {
        wsClient.send({ type: 'rejoin:cancel' });
      }
      useAppStore.getState().setRejoinOptions([]);
    };
  }, []);

  if (rejoin?.status === 'PENDING') {
    return (
      <AppShell>
        <div className="mx-auto flex h-full max-w-sm flex-col justify-center gap-6 text-center">
          <motion.div
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.8, repeat: Infinity }}
            className="font-display text-xl font-semibold"
          >
            {de.rejoin.waiting}
          </motion.div>
          <p className="text-sm text-fg-muted">{de.rejoin.waitingHint}</p>
          <Button
            variant="secondary"
            onClick={() => {
              wsClient.send({ type: 'rejoin:cancel' });
              useAppStore.getState().setRejoin(null);
              onBack();
            }}
          >
            {de.rejoin.cancel}
          </Button>
        </div>
      </AppShell>
    );
  }

  const rejected =
    rejoin?.status === 'DENIED'
      ? de.rejoin.denied
      : rejoin?.status === 'EXPIRED'
        ? de.rejoin.expired
        : null;

  return (
    <AppShell>
      <form
        className="mx-auto flex h-full max-w-sm flex-col justify-center gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          if (trimmed.length === 0) return;
          useAppStore.getState().setRejoin(null);
          wsClient.send({ type: 'rejoin:lookup', playerName: trimmed });
          setSearched(true);
        }}
      >
        <button
          type="button"
          onClick={onBack}
          className="self-start text-sm text-fg-muted hover:text-fg"
        >
          {de.common.back}
        </button>

        <div>
          <h1 className="font-display text-2xl font-bold">{de.rejoin.cta}</h1>
          <p className="mt-2 text-sm text-fg-muted">{de.rejoin.hint}</p>
        </div>

        {rejected && <p className="text-sm text-fg-muted">{rejected}</p>}

        <input
          autoFocus
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setSearched(false);
          }}
          maxLength={MAX_NAME_LENGTH}
          placeholder={de.rejoin.namePlaceholder}
          enterKeyHint="search"
          className="min-h-14 rounded-2xl border border-surface-border bg-surface px-4 text-lg text-fg outline-none placeholder:text-fg-faint focus:border-accent"
          style={{ fontSize: '16px' }}
        />

        <Button type="submit" disabled={trimmed.length === 0}>
          {de.rejoin.search}
        </Button>

        {searched && options.length === 0 && (
          <p className="text-center text-sm text-fg-faint">{de.rejoin.noMatch}</p>
        )}

        {options.length > 0 && (
          <ul className="flex flex-col gap-3">
            {options.map((option) => (
              <li
                key={option.roomId}
                className="flex items-center justify-between gap-3 rounded-2xl border border-surface-border bg-surface px-4 py-3.5"
              >
                <div className="min-w-0">
                  <div className="truncate font-display font-semibold">{option.roomName}</div>
                  <div className="text-sm text-fg-muted">
                    {de.rejoin.optionSubtitle(option.playerCount, de.phases[option.phase])}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-11 shrink-0 px-4 text-sm"
                  onClick={() =>
                    wsClient.send({
                      type: 'rejoin:request',
                      roomId: option.roomId,
                      playerName: option.playerName,
                    })
                  }
                >
                  {de.rejoin.request}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </form>
    </AppShell>
  );
}
